<template>
  <section class="p-1">
    <header class="pb-1 d-flex justify-content-between align-items-start">
      <div class="d-flex column align-items-center">
        <span class="brand-logo mr-1">
          <b-img
            id="logo"
            :src="style ? style.reducedHeaderImage : ''"
            alt="logo"
            fluid
            class="rounded"
          />
        </span>
        <h1 class="text-dark h1 mb-0">
          {{ $t('Delivery') }}
        </h1>
        <b-avatar
          size="40"
          icon="chat"
          variant="light-primary"
          badge
          class="badge-minimal ml-1"
          :badge-variant="chatWebSocketConnected ? 'success' : 'danger'"
          :title="$t('Indica o status da conexão com os chats')"
        />
        <br>
      </div>
      <div v-if="filter.storeId">
        <b-avatar
          size="40"
          icon="truck"
          variant="light-primary"
          badge
          class="badge-minimal ml-1"
          :badge-variant="deliveryWebSocketConnected ? 'success' : 'danger'"
          :title="$t('Indica o status da conexão com a atualização dos pedidos de delivery')"
        />
        &nbsp;

        <b-button
          variant="link-primary"
          :title="$t('Atualizar lista de pedidos')"
          @click="getData"
        >
          <b-icon
            icon="arrow-clockwise"
            font-scale="1"
          />
        </b-button>

        <b-button
          variant="link-primary"
          :title="$t('Testar som')"
          @click="testSound"
        >
          <b-icon
            icon="volume-up"
            font-scale="1"
          />
        </b-button>
      </div>

      <div style="min-width: 300px">
        <e-store-combo
          v-model="filter.storeId"
          :disabled="!ready"
          :required="false"
          :clearable="true"
          :only-active-options="true"
          :placeholder="ready ? $t('Selecione') : $t('Aguarde...')"
          @input="onStoreChanged"
        />
      </div>
    </header>

    <div
      v-if="!filter.storeId"
      class="text-center"
    >
      <span class="h2"> {{ needSearchMessage }}</span>
    </div>
    <div v-else>
      <motoboy-section
        v-if="motoboyEnabled"
        :loading="fetching"
      />

      <e-color-subtitled :colors="legendColors" />

      <b-tabs
        v-model="activeTabIndex"
        class="delivery-tabs"
      >
        <b-tab @click="setActiveTab('delayed')">
          <template #title>
            {{ $t('Pedidos Anteriores') }}
            <b-badge
              variant="danger"
              class="ml-1"
            >
              {{ tabBadge('delayed') }}
            </b-badge>
          </template>

          <delivery-section
            v-for="status in statusSections"
            :key="`delayed-${status.key}`"
            class="mt-3"
            :loading="fetching"
            :title-text="status.label"
            :delivery-list="ordersByTabAndStatus.delayed[status.key]"
            :empty-message="lastFetch ? status.emptyMessageDelayed : needSearchMessage"
            :now="now"
            @show-chat="showChatSidebar"
          />
        </b-tab>

        <b-tab @click="setActiveTab('today')">
          <template #title>
            {{ $t('Pedidos de Hoje') }}
            <b-badge
              variant="primary"
              class="ml-1"
            >
              {{ tabBadge('today') }}
            </b-badge>
          </template>

          <delivery-section
            v-for="status in statusSections"
            :key="`today-${status.key}`"
            class="mt-3"
            :loading="fetching"
            :title-text="status.label"
            :delivery-list="ordersByTabAndStatus.today[status.key]"
            :empty-message="lastFetch ? status.emptyMessageToday : needSearchMessage"
            :now="now"
            @show-chat="showChatSidebar"
          />
        </b-tab>

        <b-tab @click="setActiveTab('upcoming')">
          <template #title>
            {{ $t('Próximos Pedidos') }}
            <b-badge
              variant="primary"
              class="ml-1"
            >
              {{ tabBadge('upcoming') }}
            </b-badge>
          </template>

          <delivery-section
            v-for="status in statusSections"
            :key="`upcoming-${status.key}`"
            class="mt-3"
            :loading="fetching"
            :title-text="status.label"
            :delivery-list="ordersByTabAndStatus.upcoming[status.key]"
            :empty-message="lastFetch ? status.emptyMessageUpcoming : needSearchMessage"
            :now="now"
            @show-chat="showChatSidebar"
          />
        </b-tab>
      </b-tabs>
    </div>
    <chat-side-bar ref="chatSidebar" />
  </section>
</template>

<script>
import { BImg, BIcon, BButton, BAvatar, BTabs, BTab, BBadge } from 'bootstrap-vue'
import { app } from '@/mixins'
import { mapActions, mapGetters, mapState } from 'vuex'
import { EStoreCombo, EColorSubtitled } from '@/views/components'
import isElectron from 'is-electron'
import DeliverySection from './components/DeliverySection.vue'
import MotoboySection from './components/MotoboySection.vue'
import ChatSideBar from './components/ChatSideBar.vue'

// eslint-disable-next-line global-require
const alarmSound = new Audio(require('@/assets/audio/echope_pedido_delivery.mp3'))

const TAB_ORDER = ['delayed', 'today', 'upcoming']

export default {
  components: {
    BImg,
    BIcon,
    BButton,
    BTabs,
    BTab,
    BBadge,
    EStoreCombo,
    DeliverySection,
    MotoboySection,
    BAvatar,
    EColorSubtitled,
    ChatSideBar,
  },

  mixins: [app],

  data() {
    return {
      fetching: false,
      autoScroll: true,
      autoScrollIntervalId: null,
      previousCount: 0,
      orderStatus: {},
      activeTabIndex: TAB_ORDER.indexOf('today'),
      now: Date.now(),
      nowIntervalId: null,
    }
  },

  computed: {
    ...mapState('pages/pdv/payBoxConfiguration', ['pdvGlobalFilters']),
    ...mapGetters('pages/sale/deliveryDashboard', [
      'todayOrders',
      'upcomingOrders',
      'delayedOrders',
      'statusCountsByTab',
      'ordersByTabAndStatus',
      'tabBadge',
      'hasUnansweredChats',
      'ready',
      'chatWebSocketConnected',
      'deliveryWebSocketConnected',
      'motoboyEnabled',
    ]),
    ...mapState('pages/sale/deliveryDashboard', ['filter', 'lastFetch', 'activeTab']),
    ...mapState('app', ['chatApiSettings']),

    needSearchMessage() {
      return this.$t('Selecione a loja para listar os pedidos')
    },

    statusSections() {
      return [
        {
          key: 'Pending',
          label: this.$t('Pendente'),
          emptyMessageDelayed: this.$t('Sem pedidos atrasados pendentes'),
          emptyMessageToday: this.$t('Sem pedidos pendentes para hoje'),
          emptyMessageUpcoming: this.$t('Sem próximos pedidos pendentes'),
        },
        {
          key: 'Preparing',
          label: this.$t('Preparando'),
          emptyMessageDelayed: this.$t('Sem pedidos atrasados em preparação'),
          emptyMessageToday: this.$t('Sem pedidos em preparação para hoje'),
          emptyMessageUpcoming: this.$t('Sem próximos pedidos em preparação'),
        },
        {
          key: 'InProgress',
          label: this.$t('Realizando entrega'),
          emptyMessageDelayed: this.$t('Sem pedidos atrasados em entrega'),
          emptyMessageToday: this.$t('Sem pedidos em entrega para hoje'),
          emptyMessageUpcoming: this.$t('Sem próximos pedidos em entrega'),
        },
        {
          key: 'Completed',
          label: this.$t('Entrega finalizada'),
          emptyMessageDelayed: this.$t('Sem pedidos atrasados finalizados'),
          emptyMessageToday: this.$t('Sem pedidos finalizados hoje'),
          emptyMessageUpcoming: this.$t('Sem próximos pedidos finalizados'),
        },
      ]
    },

    legendColors() {
      const counts = this.statusCountsByTab[this.activeTab] || {}
      return [
        {
          backgroundStyleColor: '#ffc107',
          title: this.$t('Pendente'),
          badge: counts.Pending || 0,
        },
        {
          backgroundStyleColor: '#b8c2cc',
          title: this.$t('Preparando'),
          badge: counts.Preparing || 0,
        },
        {
          backgroundStyleColor: '#008200',
          title: this.$t('Realizando entrega'),
          badge: counts.InProgress || 0,
        },
        {
          backgroundStyleColor: 'pink',
          title: this.$t('Entrega finalizada'),
          badge: counts.Completed || 0,
        },
        {
          backgroundStyleColor: 'white',
          border: '4px solid red !important',
          title: this.$t('Chat pendente'),
          badge: counts.chatPending || 0,
          badgeVariant: 'danger',
        },
      ]
    },
  },

  watch: {
    activeTab(val) {
      const newIndex = TAB_ORDER.indexOf(val)
      if (newIndex !== -1 && this.activeTabIndex !== newIndex) {
        this.activeTabIndex = newIndex
      }
    },
    todayOrders(val) {
      if (this.fetching) {
        this.previousCount = val?.length
        val.forEach(order => {
          this.orderStatus[order.id] = 'notified'
        })
      } else {
        // eslint-disable-next-line no-lonely-if
        if (this.previousCount < val.length) {
          alarmSound.play()
          val
            .filter(order => Object.keys(this.orderStatus).indexOf(String(order.id)) < 0)
            .forEach(order => {
              this.orderStatus[order.id] = 'new'
              setInterval(() => {
                this.orderStatus[order.id] = 'notified'
              }, 10000)
            })
        }
        this.previousCount = val.length
      }
    },
    async ready(val) {
      if (val) {
        const identify = isElectron() ? await window.electronAPI.system.identifyPayboxRead() : null
        const payBoxPdvFilterStoreId = this.pdvGlobalFilters?.payBox?.store?.id || identify?.storeId
        if (payBoxPdvFilterStoreId && !this.filter.storeId) {
          console.debug(
            'auto-select store config to PDV current payBoxPdvFilterStoreId',
            payBoxPdvFilterStoreId
          )
          this.setFilter({
            ...this.filter,
            storeId: payBoxPdvFilterStoreId.toString(),
          })
          await this.getData()
        }
      }
    },
    hasUnansweredChats(val) {
      if (!val) {
        this.setLastTimeHasUnansweredChats(null)
      } else {
        this.setLastTimeHasUnansweredChats(Date.now())
      }
    },
  },

  async mounted() {
    await this.fetchDeliveryWebSocketConfig()
    await this.connectToChatWebSocket()
    await this.connectToDeliveryWebSocket()
    this.runUnansweredMessageAlarm()
    this.nowIntervalId = setInterval(() => {
      this.now = Date.now()
    }, 60000)
  },

  beforeDestroy() {
    this.disconnectDashboardWebSockets()
    if (this.nowIntervalId) {
      clearInterval(this.nowIntervalId)
      this.nowIntervalId = null
    }
  },

  methods: {
    ...mapActions('pages/sale/deliveryDashboard', [
      'fetchDeliveryOrders',
      'fetchMotoboys',
      'fetchDeliveryWebSocketConfig',
      'loadActiveChats',
      'connectToChatWebSocket',
      'connectToDeliveryWebSocket',
      'joinChatChannel',
      'joinDeliveryChannel',
      'sendChatMessage',
      'setLastTimeHasUnansweredChats',
      'setFilter',
      'setActiveTab',
      'runUnansweredMessageAlarm',
      'disconnectDashboardWebSockets',
    ]),

    async getData() {
      try {
        this.fetching = true
        // Loja sem a funcionalidade de Motoboys não carrega a lista: o painel não
        // exibe a seção nem a designação nos cards.
        await Promise.all([
          this.fetchDeliveryOrders(),
          ...(this.motoboyEnabled ? [this.fetchMotoboys()] : []),
        ])
        await this.joinChatChannel()
        await this.joinDeliveryChannel()
        this.loadActiveChats()
      } catch (error) {
        this.showError({ error })
      } finally {
        this.fetching = false
      }
    },

    async onStoreChanged() {
      await this.disconnectDashboardWebSockets()
      await this.getData()
      await this.connectToChatWebSocket()
      await this.connectToDeliveryWebSocket()
      this.runUnansweredMessageAlarm()
    },

    testSound() {
      alarmSound.play()
    },
    scrollToBottom() {
      if (this.autoScroll) {
        const { content } = this.$refs
        const { scrollHeight } = content
        const { scrollTop } = content
        const { clientHeight } = content
        const remainingScroll = scrollHeight - (scrollTop + clientHeight)
        if (remainingScroll > 0) {
          content.scrollBy(0, 10)
        } else {
          clearInterval(this.autoScrollIntervalId)
          this.autoScrollIntervalId = null
          setTimeout(() => {
            // Scroll back to the top after 2 seconds
            content.scrollTo(0, 0)
            this.autoScrollIntervalId = setInterval(this.scrollToBottom, 50)
          }, 2000)
        }
      }
    },

    toggleAutoScroll() {
      if (this.autoScrollIntervalId) {
        clearInterval(this.autoScrollIntervalId)
        this.autoScrollIntervalId = null
        this.autoScroll = false
      } else {
        this.autoScrollIntervalId = setInterval(this.scrollToBottom, 50)
        this.autoScroll = true
      }
    },

    showChatSidebar(order) {
      console.log('showChat', order)
      this.$refs.chatSidebar.show(order)
    },
  },
}
</script>

<style lang="scss" scoped>
.brand-logo {
  box-sizing: border-box;
  width: 35px;
}
</style>
